import AuthBootstrapGate from "@components/auth/AuthBootstrapGate";
import { ContractModal } from "@components/ui";
import { contractService, ContractStatus } from "@services/contractService";
import { useAuthStore } from "@store/authStore";
import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

// Layouts
import AuthLayout from "@components/layouts/AuthLayout";
import MainLayout from "@components/layouts/MainLayout";

// Pages
import {
    AdminClassesPage,
    AdminContractsPage,
    AdminDashboardPage,
    AdminExtraMaterialsPage,
    AdminHomeworksPage,
    AdminLessonsPage,
    AdminLevelsPage,
    AdminPricingPage,
    AdminReadersPage,
    AdminStudentsPage,
    AdminTeachersPage,
} from "@pages/admin";
import LoginPage from "@pages/auth/LoginPage";
import DiagnosticsPage from "@pages/DiagnosticsPage";
import { DashboardPage } from "@pages/dashboard/DashboardPage";
import { FinancialPage } from "@pages/financial/FinancialPage";
import { LessonsPage } from "@pages/lessons/LessonsPage";
import ResourceViewerPage from "@pages/lessons/ResourceViewerPage";
import NotFoundPage from "@pages/NotFoundPage";
import { ProfilePage } from "@pages/profile/ProfilePage";
import { ReadersPage } from "@pages/readers/ReadersPage";
import {
    AttendancePage,
    TeacherCalendarPage,
    TeacherClassesPage,
    TeacherDashboardPage,
    TeacherStudentsPage,
} from "@pages/teacher";

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  const [contractStatus, setContractStatus] = useState<ContractStatus | null>(
    null,
  );
  const [showContractModal, setShowContractModal] = useState(false);

  const checkContractStatus = useCallback(async () => {
    if (!isAuthenticated || user?.role !== "student") {
      return;
    }

    try {
      const status = await contractService.getContractStatus();
      setContractStatus(status);
      setShowContractModal(status.needsSignature);
    } catch {
      // Silently fail - user still gets the app
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    checkContractStatus();
  }, [checkContractStatus]);

  const handleContractSigned = () => {
    setShowContractModal(false);
    setContractStatus((prev) =>
      prev ? { ...prev, needsSignature: false } : null,
    );
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthBootstrapGate>
      {children}
      <ContractModal
        isOpen={showContractModal && contractStatus?.needsSignature === true}
        onContractSigned={handleContractSigned}
      />
    </AuthBootstrapGate>
  );
}

// Role guards below render under ProtectedRoute → AuthBootstrapGate, so
// `user` is always populated here; they only enforce role-based redirects.
function StudentRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);

  if (user?.role !== "student") {
    return <Navigate to="/teacher" replace />;
  }

  return <>{children}</>;
}

function TeacherRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);

  if (user?.role !== "teacher" && user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Public Route Component (redirect if authenticated). Not under the gate, so
// it must not read user.role — IndexRedirect owns the role → home mapping.
function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Director Only Route Component (only for staff users - Elienai)
function DirectorRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);

  if (!user?.isStaff) {
    return <Navigate to="/teacher" replace />;
  }

  return <>{children}</>;
}

function IndexRedirect() {
  const user = useAuthStore((state) => state.user);

  if (user?.role === "teacher" || user?.role === "admin") {
    return <Navigate to="/teacher" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          </PublicRoute>
        }
      />

      {/* Dependency-free connectivity diagnostics — no auth, no API required */}
      <Route path="/diagnostics" element={<DiagnosticsPage />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<IndexRedirect />} />
        <Route
          path="dashboard"
          element={
            <StudentRoute>
              <DashboardPage />
            </StudentRoute>
          }
        />
        <Route
          path="financial"
          element={
            <StudentRoute>
              <FinancialPage />
            </StudentRoute>
          }
        />
        <Route
          path="lessons"
          element={
            <StudentRoute>
              <LessonsPage />
            </StudentRoute>
          }
        />
        <Route
          path="lessons/view"
          element={
            <StudentRoute>
              <ResourceViewerPage />
            </StudentRoute>
          }
        />
        <Route path="readers" element={<ReadersPage />} />
        <Route path="profile" element={<ProfilePage />} />
        {/* Teacher Routes */}
        <Route
          path="teacher"
          element={
            <TeacherRoute>
              <TeacherDashboardPage />
            </TeacherRoute>
          }
        />
        <Route
          path="teacher/classes"
          element={
            <TeacherRoute>
              <TeacherClassesPage />
            </TeacherRoute>
          }
        />
        <Route
          path="teacher/attendance"
          element={
            <TeacherRoute>
              <AttendancePage />
            </TeacherRoute>
          }
        />
        <Route
          path="teacher/calendar"
          element={
            <TeacherRoute>
              <TeacherCalendarPage />
            </TeacherRoute>
          }
        />
        <Route
          path="teacher/extra-materials"
          element={
            <TeacherRoute>
              <AdminExtraMaterialsPage />
            </TeacherRoute>
          }
        />
        <Route
          path="teacher/students"
          element={
            <DirectorRoute>
              <TeacherStudentsPage />
            </DirectorRoute>
          }
        />
        {/* Admin Routes (Director Only) */}
        <Route
          path="admin"
          element={
            <DirectorRoute>
              <AdminDashboardPage />
            </DirectorRoute>
          }
        />
        <Route
          path="admin/classes"
          element={
            <DirectorRoute>
              <AdminClassesPage />
            </DirectorRoute>
          }
        />
        <Route
          path="admin/students"
          element={
            <DirectorRoute>
              <AdminStudentsPage />
            </DirectorRoute>
          }
        />
        <Route
          path="admin/teachers"
          element={
            <DirectorRoute>
              <AdminTeachersPage />
            </DirectorRoute>
          }
        />
        <Route
          path="admin/levels"
          element={
            <DirectorRoute>
              <AdminLevelsPage />
            </DirectorRoute>
          }
        />
        <Route
          path="admin/lessons"
          element={
            <DirectorRoute>
              <AdminLessonsPage />
            </DirectorRoute>
          }
        />
        <Route
          path="admin/homeworks"
          element={
            <DirectorRoute>
              <AdminHomeworksPage />
            </DirectorRoute>
          }
        />
        <Route
          path="admin/extra-materials"
          element={
            <DirectorRoute>
              <AdminExtraMaterialsPage />
            </DirectorRoute>
          }
        />
        <Route
          path="admin/readers"
          element={
            <DirectorRoute>
              <AdminReadersPage />
            </DirectorRoute>
          }
        />
        <Route
          path="admin/contracts"
          element={
            <DirectorRoute>
              <AdminContractsPage />
            </DirectorRoute>
          }
        />
        <Route
          path="admin/pricing"
          element={
            <DirectorRoute>
              <AdminPricingPage />
            </DirectorRoute>
          }
        />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
